import { saveOperationLogApi } from '@/services/Applog/ApplogService';

/**
 * 专家操作日志工具
 */

/**
 * 专家操作日志上报辅助函数
 * @param {Object} options - 日志参数
 * @param {string} options.entityId - 专家ID
 * @param {string} options.operationType - 操作类型: 'create', 'update', 'change_phone'
 * @param {string} options.operationDesc - 操作描述
 * @param {Object} options.extra - 扩展信息字段（可选）
 * @param {string} options.result - 操作结果: 'success' 或 'failed'
 * @param {Object} options.dataSnapshot - 数据快照（可选）
 * @returns {Promise} 返回操作结果
 */
export const logExpertOperation = async options => {
    const { entityId, operationType, operationDesc, extra = null, result = 'success', dataSnapshot = null } = options;

    // 构建日志数据
    const logData = {
        entityType: 'expert',
        entityId,
        module: 'expert',
        portal: 'boss', // 端口标识：用于区分同一个 module 在不同端口。
        operationType,
        operationDesc,
        extra: extra ? JSON.stringify(extra) : null,
        result,
        dataSnapshot: dataSnapshot ? JSON.stringify(dataSnapshot) : null,
        memcard: null,
    };

    try {
        const response = await saveOperationLogApi(logData);
        console.log('专家操作日志上报成功', logData);
        return response;
    } catch (error) {
        console.error('专家操作日志上报失败', error, logData);
        throw error;
    }
};

/**
 * 新增专家日志
 * @param {number} entityId - 专家ID
 * @param {Object} afterData - 新增后的专家信息
 * @param {boolean} isSuccess - 是否成功
 * @param {string} errorMessage - 失败时的错误信息
 */
export const logExpertCreate = async (entityId, afterData, isSuccess = true, errorMessage = null) => {
    const extra = isSuccess ? null : { error_message: errorMessage };
    const dataSnapshot = isSuccess ? { before: null, after: afterData } : null;

    return logExpertOperation({
        entityId,
        operationType: 'create',
        operationDesc: '新增专家',
        extra,
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot,
    });
};

/**
 * 编辑专家日志
 * @param {number} entityId - 专家ID
 * @param {Object} beforeData - 编辑前的专家信息
 * @param {Object} afterData - 编辑后的专家信息
 * @param {boolean} isSuccess - 是否成功
 * @param {string} errorMessage - 失败时的错误信息
 */
export const logExpertUpdate = async (entityId, beforeData, afterData, isSuccess = true, errorMessage = null) => {
    const extra = isSuccess ? null : { error_message: errorMessage };
    const dataSnapshot = isSuccess ? { before: beforeData, after: afterData } : null;

    return logExpertOperation({
        entityId,
        operationType: 'update',
        operationDesc: '编辑专家',
        extra,
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot,
    });
};

/**
 * 更换手机号日志
 * @param {number} entityId - 专家ID
 * @param {string} oldMobile - 原手机号
 * @param {string} newMobile - 新手机号
 * @param {boolean} isSuccess - 是否成功
 * @param {string} errorMessage - 失败时的错误信息
 */
export const logExpertChangeMobile = async (entityId, oldMobile, newMobile, isSuccess = true, errorMessage = null) => {
    const extra = isSuccess ? null : { error_message: errorMessage };

    return logExpertOperation({
        entityId,
        operationType: 'change_phone',
        operationDesc: `更换手机号，原手机号 ${oldMobile}，更换为 ${newMobile}`,
        extra,
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: null,
    });
};

/**
 * 启用/禁用专家状态日志（禁用理由写入 extra，供审计）
 * @param {number} entityId - 专家 ID
 * @param {{ fromStatus: number, toStatus: number, disableReason?: string }} payload - status: 0 正常，1 禁用
 * @param {boolean} isSuccess - 是否成功
 * @param {string|null} errorMessage - 失败时的错误信息
 */
export const logExpertChangeStatus = async (entityId, { fromStatus, toStatus, disableReason = null }, isSuccess = true, errorMessage = null) => {
    const baseExtra = isSuccess
        ? {
              from_status: fromStatus,
              to_status: toStatus,
              ...(toStatus === 1 && disableReason ? { disable_reason: disableReason } : {}),
          }
        : {
              error_message: errorMessage,
              from_status: fromStatus,
              to_status: toStatus,
          };

    const operationDesc = toStatus === 1 ? '禁用专家' : '启用专家';

    return logExpertOperation({
        entityId,
        operationType: 'change_status',
        operationDesc,
        extra: baseExtra,
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: null,
    });
};

/**
 * 调整专家可信度日志（operationDesc 为模板；可信度存枚举值，展示时由 parseOperationTemplate 按 credibilityList 解析）
 * @param {number} entityId - 专家 ID
 * @param {{ fromCredibility: string|null, toCredibility: string, adjustReason: string }} payload
 * @param {boolean} isSuccess - 是否成功
 * @param {string|null} errorMessage - 失败时的错误信息
 */
export const logExpertChangeCredibility = async (entityId, { fromCredibility, toCredibility, adjustReason }, isSuccess = true, errorMessage = null) => {
    const extra = isSuccess
        ? {
              from_credibility: fromCredibility,
              to_credibility: toCredibility,
              adjust_reason: adjustReason,
          }
        : {
              error_message: errorMessage,
              from_credibility: fromCredibility,
              to_credibility: toCredibility,
              adjust_reason: adjustReason,
          };

    const operationDesc = '调整专家可信度：{{from_credibility}} → {{to_credibility}}，调整理由：{{adjust_reason}}';

    return logExpertOperation({
        entityId,
        operationType: 'change_credibility',
        operationDesc,
        extra,
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: null,
    });
};
